'use client';
import {
  BrandIndexCard,
  BrandIndexCardMedia,
  BrandIndexCardMediaPlaceholder,
  BrandIndexCardName,
  BrandIndexCardSkeleton,
  BrandIndexGridCount,
  BrandIndexGridItem,
  BrandIndexGridList,
} from '@vs-shop/commerce-ui/brandIndexGrid';
import { EmptyState, EmptyStateVariant } from '@vs-shop/commerce-ui/emptyState';
import { ImageMissingPlaceholder } from '@vs-shop/ui/imageMissingPlaceholder';
import { Chunk } from 'effect';
import { useIntl } from 'react-intl';

import { brandsPerPageDefault } from '../../../../config/brands';

import { resolveImageUrl } from '@vs-shop/engine/image/resolveImageUrl';

import { IntlDecimal } from '../../../server/intl/Intl';

import { fetchBrandsByPageAtom } from '../../../../effect/atom/brand/fetchBrandsByPage';
import { loadMorePagesAtom } from '@vs-shop/engine/atom/brand/pagination';
import { ImageFilter } from '@vs-shop/engine/image/ImageFilter';
import * as AsyncResult from 'effect/unstable/reactivity/AsyncResult';
import { useAtomValue } from '@effect/atom-react';

type Props = {
  currentPage: number;
  alphabetFilter: string | null;
  searchFilter: string | null;
};

export default function BrandsGrid({ currentPage, alphabetFilter, searchFilter }: Props) {
  const intl = useIntl();

  const brandsPerPageResponse = useAtomValue(
    fetchBrandsByPageAtom(currentPage, alphabetFilter, searchFilter)
  );
  const loadMorePages = useAtomValue(loadMorePagesAtom);

  return AsyncResult.builder(brandsPerPageResponse)
    .onInitial(() => {
      return (
        <div data-slot="brands-grid-container" data-state="loading">
          <BrandIndexGridList aria-busy="true">
            {Array.from({ length: brandsPerPageDefault }, (_, index) => (
              <BrandIndexGridItem key={`skeleton-${String(index)}`}>
                <BrandIndexCardSkeleton />
              </BrandIndexGridItem>
            ))}
          </BrandIndexGridList>
        </div>
      );
    })
    .onSuccess((success) => {
      const totalMembers = success['hydra:totalItems'];

      if (totalMembers === 0) {
        return (
          <EmptyState variant={EmptyStateVariant.INLINE} className="p-4 text-xl">
            {intl.formatMessage({
              id: 'component.client.page.brands.brandsGrid.noBrands',
              defaultMessage: 'Nenalezeny žádné značky',
            })}
          </EmptyState>
        );
      }

      const showCountFoundText = alphabetFilter === null && searchFilter === null;

      return (
        <div data-slot="brands-grid-container" data-state="ready">
          {showCountFoundText ? (
            <BrandIndexGridCount data-variant="default">
              {intl.formatMessage({
                id: 'component.client.page.brands.brandList.favoriteBrands',
                defaultMessage: 'Oblíbené značky',
              })}
            </BrandIndexGridCount>
          ) : (
            <BrandIndexGridCount data-variant="filtered">
              {intl.formatMessage(
                {
                  id: 'component.client.page.brands.brandList.count.found',
                  defaultMessage:
                    '{count, plural, =0 {Nenalezena žádná značka} one {Nalezena {count} značka} few {Nalezeny {count} značky} other {Nalezeno {count} značek}}',
                },
                { count: <IntlDecimal key="count" value={totalMembers} /> }
              )}
            </BrandIndexGridCount>
          )}

          <BrandIndexGridList>
            {Chunk.range(currentPage, currentPage + loadMorePages).pipe(
              Chunk.map((page) => (
                <BrandsGridPerPage
                  key={page}
                  page={page}
                  alphabetFilter={alphabetFilter}
                  searchFilter={searchFilter}
                />
              )),
              Chunk.toReadonlyArray
            )}
          </BrandIndexGridList>
        </div>
      );
    })
    .render();
}

type BrandsGridPerPageProps = {
  page: number;
  alphabetFilter: string | null;
  searchFilter: string | null;
};

function BrandsGridPerPage({ page, alphabetFilter, searchFilter }: BrandsGridPerPageProps) {
  const brandsPerPageResponse = useAtomValue(
    fetchBrandsByPageAtom(page, alphabetFilter, searchFilter)
  );

  const brandMembers = AsyncResult.match(brandsPerPageResponse, {
    onInitial: () => [],
    onFailure: () => [],
    onSuccess: ({ value }) => value['hydra:member'],
  });

  // Show skeleton cards while loading individual pages
  if (AsyncResult.isInitial(brandsPerPageResponse)) {
    return Array.from({ length: brandsPerPageDefault }, (_, index) => (
      <BrandIndexGridItem key={`page-${String(page)}-skeleton-${String(index)}`}>
        <BrandIndexCardSkeleton />
      </BrandIndexGridItem>
    ));
  }

  return (
    <>
      {brandMembers.map((brand) => {
        const imagePath = brand.image?.pathAbstract ?? null;

        const imageAbstractPath =
          imagePath !== null ? resolveImageUrl(imagePath, ImageFilter.brand_list) : null;

        return (
          <BrandIndexGridItem key={brand.id}>
            <BrandIndexCard
              href={brand.routerUrl}
              aria-label={brand.name ?? undefined}
              prefetch={false}
            >
              {imageAbstractPath !== null ? (
                <BrandIndexCardMedia
                  src={imageAbstractPath}
                  alt={brand.name ?? ''}
                  width={100}
                  height={100}
                />
              ) : (
                <BrandIndexCardMediaPlaceholder>
                  <ImageMissingPlaceholder />
                </BrandIndexCardMediaPlaceholder>
              )}
              <BrandIndexCardName>{brand.name}</BrandIndexCardName>
            </BrandIndexCard>
          </BrandIndexGridItem>
        );
      })}
    </>
  );
}
